import React, { useEffect, useState, useCallback, useMemo } from "react";
import { message, Input, Table, Space, Popconfirm, Button, Select, Modal, Form } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, MinusCircleOutlined, CopyOutlined } from "@ant-design/icons";

import { getEquipmentAccessories, deleteEquipmentAccessories, createEquipmentAccessories, updateEquipmentAccessories } from "../../api/equipmentAccessories";
import { getEquipments } from "../../api/equipment";

import PageHeader from "../../components/shared/PageHeader";
import BlueButton from "../../components/shared/BlueButton";
import { PER_PAGE } from "../../configs";

const { Search } = Input;

export default function EquipmentAccessoriesView() {
    const perPage = PER_PAGE;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [accessories, setAccessories] = useState(null);
    const [editAccessory, setEditAccessory] = useState(null);
    const [showAccessoriesForm, setShowAccessoriesForm] = useState(false);
    const [searchEquipment, setSearchEquipment] = useState(null);

    const fetchAccessories = useCallback(async () => {
        try {
            setIsLoading(true);
            const { total, results } = await getEquipmentAccessories({ q: searchEquipment, page, perPage });
            setTotal(total);
            setAccessories(results);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }, [searchEquipment, page, perPage]);

    const handleDelete = async (worklog) => {
        await deleteEquipmentAccessories(worklog.id);
        fetchAccessories({ page, perPage });
    };

    useEffect(() => {
        fetchAccessories({ page, perPage });
    }, [page, perPage, fetchAccessories]);


    const tableCols = [
        {
            title: "No", dataIndex: "no", key: "no", width: 80, render: (text, record, index) => {
                let no = (page - 1) * perPage + index + 1;
                return <p>{no}</p>;
            },
        },
        { title: "Equipment", dataIndex: ["equipment", 'name'], key: ["equipment", 'name'], width: 200 },
        {
            title: "Accessories", dataIndex: "accessories", key: "accessories",
            render: accessories => {
                return (
                    <div>
                        {accessories.map(accessory => accessory).join(', ')}
                    </div>)
            }
        },
        {
            title: "Action",
            key: "action",
            width: 150,
            render: (text, record, index) => (
                <Space size={2}>
                    <Popconfirm
                        title="Delete the task"
                        description="Are you sure to delete this worklog?"
                        onConfirm={() => handleDelete(accessories[index])}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button type="text" icon={<DeleteOutlined />} danger></Button>
                    </Popconfirm>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => {
                            setEditAccessory(accessories[index])
                            setShowAccessoriesForm(true)
                        }}
                    />
                    <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => {
                            const { accessories: a } = accessories[index]
                            setEditAccessory({ accessories: a })
                            setShowAccessoriesForm(true)
                        }}
                    />
                </Space>
            ),
        },
    ]

    return (
        <main className="min-h-full flex flex-col space-y-6">
            <PageHeader title="Equipment Accessories" home="App" route="/" />

            <div className="flex-grow bg-white ml-6 p-6 flex flex-col space-y-6">

                <div className="flex justify-between">
                    <BlueButton type="primary" onClick={() => {
                        setEditAccessory(null);
                        setShowAccessoriesForm(true)
                    }}>Add Equipment Accessories</BlueButton>

                    {/* TODO: Add Search */}
                    {/* <Search
                        placeholder="Search Equipment"
                        style={{
                            width: 200,
                        }}
                        allowClear
                        onSearch={(q) => setSearchEquipment(q)}
                    /> */}
                </div>

                <Table
                    dataSource={accessories && accessories.map((equipmentAccessory, index) => {
                        return {
                            no: index + 1,
                            ...equipmentAccessory,
                        }
                    })}
                    columns={tableCols}
                    pagination={{
                        size: "small",
                        total,
                        pageSize: perPage,
                        current: parseInt(page),
                        onChange: setPage,
                        showQuickJumper: true,
                        showTotal: () => `Total ${total} worklogs`,
                        position: ["bottomLeft"],
                    }}
                    loading={isLoading}
                />

                <AccessoriesForm
                    open={showAccessoriesForm}
                    initialValues={editAccessory}
                    close={async () => {
                        setShowAccessoriesForm(false);
                        setEditAccessory(null);
                        fetchAccessories({ page, perPage });
                    }} />

            </div>
        </main>
    );

}

const AccessoriesForm = ({ open, close, initialValues }) => {
    const [form] = Form.useForm();
    const [equipmentOptions, setEquipmentOptions] = useState([]);

    const fetchEquipments = useCallback(async ({ q }) => {
        try {
            const { results } = await getEquipments({ q });
            setEquipmentOptions(results);
        } catch (err) {
            console.error(err);
            message.error("Something went wrong");
        }
    }, []);

    const equipmentSearchOptions = useMemo(() => {
        return equipmentOptions?.map((equipment) => {
            return { label: equipment.name, value: equipment.id };
        });
    }, [equipmentOptions])


    const handleSubmit = async () => {
        form.validateFields().then(async (values) => {
            try {
                if (initialValues?.id) {
                    const res = await updateEquipmentAccessories(initialValues.id, values);
                    if (res.Code === 1) {
                        message.success("Equipment Accessories updated successfully");
                        form.resetFields();
                        close();
                    } else {
                        message.error(res.Msg);
                    }
                } else {
                    const res = await createEquipmentAccessories(values);
                    if (res.Code === 1) {
                        message.success("Equipment Accessories created successfully");
                        form.resetFields();
                        close();
                    } else {
                        message.error(res.Msg);
                    }
                }
            } catch (error) {
                if (error.status === 409) {
                    message.warning("Equipment already exist");
                } else {
                    message.error("Something went wrong");
                }
            }
        })
    }

    useEffect(() => {
        fetchEquipments({});
        if (initialValues) {
            form.setFieldsValue({ ...initialValues });
            if (initialValues?.equipment)
                form.setFieldValue("equipmentId", initialValues?.equipment.id);
        } else {
            form.resetFields();
            form.setFieldValue("accessories", []);
        }
    }, [fetchEquipments, form, initialValues]);

    return (
        <Modal getContainer={false}
            title="Create Accessories"
            open={open}
            onCancel={() => {
                form.resetFields();
                close();
            }}
            onOk={handleSubmit}
        >
            <Form
                form={form}
                layout="vertical"
                className="mt-6 overflow-y-scroll"
            >
                <Form.Item name="equipmentId" label="Equipment" required initialValue={initialValues?.equipment?.id}>
                    <Select
                        placeholder="Search Equipment"
                        onSearch={(str) => fetchEquipments({ q: str })}
                        filterOption={false}
                        options={equipmentSearchOptions}
                        showSearch
                    ></Select>
                </Form.Item>

                <Form.List name="accessories" initialValue={initialValues?.accessories}>
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map(({ key, name, ...restField }) => (
                                <div className="flex items-center relative w-full ">
                                    <Form.Item
                                        {...restField}
                                        name={[name]}
                                        key={key}
                                        required
                                        className="w-full pr-6"
                                    >
                                        <Input placeholder="Accessory" />
                                    </Form.Item>

                                    <MinusCircleOutlined
                                        onClick={() => remove(name)}
                                        className="absolute top-2 right-0 "
                                    />
                                </div>
                            ))}
                            <Form.Item>
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Add Accessory
                                </Button>
                            </Form.Item>

                        </>
                    )}
                </Form.List>

            </Form>
        </Modal>
    )
}